import React, { useCallback, useMemo, useState } from 'react'
import { placeholders } from '../../../../urls'
import { type AgentActivityCard, type AgentActivityTaskItem } from './agentActivity.types'

interface AgentActivityListProps {
    cards: AgentActivityCard[]
    onTaskClick: (item: AgentActivityTaskItem) => void
    compact?: boolean
}

export default function AgentActivityList({ cards, onTaskClick, compact = false }: AgentActivityListProps): JSX.Element {
    const [openAgentIds, setOpenAgentIds] = useState<Set<number>>(new Set())

    const toggleAgentCard = useCallback((agentId: number) => {
        setOpenAgentIds((prev) => {
            const next = new Set(prev)
            if (next.has(agentId)) {
                next.delete(agentId)
            } else {
                next.add(agentId)
            }
            return next
        })
    }, [])

    const rankedCards = useMemo(() => cards.map((card, index) => {
        const rank = index < 3 ? index + 1 : null
        const sectionTotals = {
            viewings: card.sections.find((section) => section.key === 'viewings')?.total ?? 0,
            offers: card.sections.find((section) => section.key === 'offers')?.total ?? 0,
            adverts: card.sections.find((section) => section.key === 'adverts')?.total ?? 0
        }

        let achievement = 'Top Performer'
        if (sectionTotals.viewings >= sectionTotals.offers && sectionTotals.viewings >= sectionTotals.adverts && sectionTotals.viewings > 0) {
            achievement = 'Viewing Monster'
        } else if (sectionTotals.offers >= sectionTotals.viewings && sectionTotals.offers >= sectionTotals.adverts && sectionTotals.offers > 0) {
            achievement = 'Deal Closer'
        } else if (sectionTotals.adverts > 0) {
            achievement = 'Ad Machine'
        }

        return { card, rank, achievement }
    }), [cards])

    if (cards.length === 0) {
        return (
            <div className={'schedule-desktop__empty'} >
                <img
                    className="schedule-desktop__empty-image"
                    src={placeholders.Notices}
                    alt={'liable_picture'}
                />
                <span className="schedule-desktop__empty-label">
                    No agent activity found for current filters
                </span>
            </div>
        )
    }

    return (
        <div className={`agent-activity ${compact ? 'agent-activity--compact' : ''}`}>
            {rankedCards.map(({ card, rank, achievement }) => (
                <div
                    key={`agent-activity-${String(card.agentId)}`}
                    className={`agent-activity__card card shadow-sm ${rank != null ? `agent-activity__card--rank-${String(rank)}` : ''}`}
                >
                    {rank != null
                        ? <div className={`agent-activity__rank-badge agent-activity__rank-badge--${String(rank)}`}>
                            <span className="agent-activity__rank-title">{`Rank #${String(rank)}`}</span>
                            <span className="agent-activity__rank-achievement">{achievement}</span>
                        </div>
                        : null}
                    <button
                        type="button"
                        className="agent-activity__toggle-icon"
                        aria-label={openAgentIds.has(card.agentId) ? 'Collapse agent activity' : 'Expand agent activity'}
                        onClick={() => { toggleAgentCard(card.agentId) }}
                    >
                        {openAgentIds.has(card.agentId) ? '-' : '+'}
                    </button>

                    <button
                        type="button"
                        className="agent-activity__header-button"
                        onClick={() => { toggleAgentCard(card.agentId) }}
                    >
                        <div className="agent-activity__header">
                            <img src={card.avatar} className="agent-activity__avatar" alt={card.fullName} />
                            <div className="agent-activity__meta">
                                <div className="agent-activity__name-row">
                                    <span className="agent-activity__name">{card.fullName}</span>
                                    <span className="badge badge-light-primary">{card.totalActivity}</span>
                                </div>
                                <span className="agent-activity__line">{`Company: ${card.company}`}</span>
                                <span className="agent-activity__line">{`DOB: ${card.dateOfBirth}`}</span>
                                <span className="agent-activity__line">{`Registered: ${card.registrationDate}`}</span>
                            </div>
                        </div>
                    </button>

                    {openAgentIds.has(card.agentId)
                        ? <div className="agent-activity__sections">
                            {card.sections.map((section) => (
                                <div key={`${String(card.agentId)}-${section.key}`} className={`agent-activity__section agent-activity__section--${section.key}`}>
                                    <div className="agent-activity__section-title">
                                        <span>{section.title}</span>
                                        <span className="agent-activity__count-badge">{section.total}</span>
                                    </div>
                                    <div className="agent-activity__items">
                                        {section.items.map((item) => (
                                            <button
                                                type="button"
                                                key={item.id}
                                                className="agent-activity__item"
                                                onClick={() => { onTaskClick(item) }}
                                            >
                                                <span className="agent-activity__item-title">{item.title}</span>
                                                <span className="agent-activity__item-subtitle">{item.subtitle}</span>
                                                <span className="agent-activity__item-status">{item.status}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        : null}
                </div>
            ))}
        </div>
    )
}
